import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, LogOut, BookOpen, Pencil, Sparkles, Lock, Gift, Copy, Check, FileText, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

function ReferralCard({ referralCode, credit }) {
  const [copied, setCopied] = useState(false);
  if (!referralCode) return null;
  const link = `${window.location.origin}/the-menopause-invitation?ref=${referralCode}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — try selecting and copying manually.");
    }
  };

  return (
    <div className="mt-10 bg-cream-soft border border-hairline rounded-2xl p-6 md:p-8" data-testid="tmi-dashboard-referral">
      <div className="flex items-start gap-4">
        <span className="w-10 h-10 rounded-full bg-gold/15 text-gold-dark inline-flex items-center justify-center flex-shrink-0">
          <Gift size={18} strokeWidth={1.5} />
        </span>
        <div className="flex-1">
          <div className="eyebrow text-sage mb-1">Share with a friend</div>
          <h3 className="font-serif-display text-xl text-ink leading-tight">
            Pass it on — and earn $10 each time.
          </h3>
          <p className="mt-2 text-sm text-ink-soft leading-relaxed max-w-md">
            If a friend joins through your link, you earn $10 AUD credit toward your next program.
            Simple as that.
          </p>
          {credit > 0 && (
            <div className="mt-3 inline-flex items-center gap-2 bg-sage/10 text-sage text-xs font-medium px-3 py-1.5 rounded-full" data-testid="tmi-dashboard-referral-credit">
              <Check size={12} strokeWidth={2} /> ${credit.toFixed(2)} AUD credit waiting
            </div>
          )}
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <input
              readOnly
              value={link}
              className="flex-1 bg-white border border-hairline rounded-full px-4 py-2.5 text-xs text-ink-soft font-mono"
              data-testid="tmi-dashboard-referral-link"
              onFocus={(e) => e.target.select()}
            />
            <button
              onClick={copy}
              className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-4 py-2.5 text-sm transition-all whitespace-nowrap"
              data-testid="tmi-dashboard-referral-copy"
            >
              {copied ? <Check size={14} strokeWidth={1.8} /> : <Copy size={14} strokeWidth={1.8} />}
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MenopauseDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const hasAccess = user && (user.has_menopause_invitation_access || user.role === "admin");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const endpoint = hasAccess ? "/menopause/modules" : "/menopause/preview";
        const { data } = await api.get(endpoint);
        if (!cancelled) setModules(data);

        if (hasAccess) {
          try {
            const ans = await api.get("/menopause/answers");
            if (!cancelled) setAnswers(ans.data || {});
          } catch {
            /* not fatal */
          }
        }
      } catch {
        if (!cancelled) setModules([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [hasAccess]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const totalQuestions = (modules || []).reduce(
    (acc, m) => acc + (m.questions?.length || 0),
    0,
  );
  const answeredCount = Object.values(answers).reduce(
    (acc, m) =>
      acc + Object.values(m || {}).filter((v) => v && v.trim().length > 0).length,
    0,
  );

  return (
    <div className="min-h-screen bg-cream flex" data-testid="page-tmi-dashboard">
      <aside className="hidden lg:flex w-72 bg-cream-soft border-r border-hairline flex-col">
        <div className="p-8 border-b border-hairline">
          <Link to="/" className="font-serif-display text-2xl text-ink">
            Pip<span className="text-gold italic"> Bennett</span>
          </Link>
          <div className="mt-6">
            <div className="eyebrow text-ink-soft">Signed in as</div>
            <div className="mt-1 text-ink font-medium truncate" data-testid="tmi-sidebar-user">
              {user?.name || user?.email}
            </div>
          </div>
        </div>
        <nav className="flex-1 p-6 space-y-2">
          <div className="eyebrow text-ink-soft mb-4 px-3">The Menopause Invitation</div>
          <Link
            to="/dashboard/menopause"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-sage/10 text-sage font-medium"
            data-testid="tmi-sidebar-overview"
          >
            <Sparkles size={16} strokeWidth={1.5} /> Overview
          </Link>
          {(modules || []).map((m) => (
            <Link
              key={m.id}
              to={hasAccess ? `/dashboard/menopause/module/${m.id}` : "/the-menopause-invitation"}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-ink-soft hover:bg-cream hover:text-ink transition text-sm"
              data-testid={`tmi-sidebar-module-${m.id}`}
            >
              {hasAccess ? <BookOpen size={14} strokeWidth={1.5} /> : <Lock size={14} strokeWidth={1.5} />}
              <span className="truncate">{m.title}</span>
            </Link>
          ))}
          <div className="pt-6 mt-4 border-t border-hairline/60">
            <Link
              to="/dashboard"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-ink-soft hover:text-ink text-sm"
              data-testid="tmi-sidebar-mf-link"
            >
              <ArrowRight size={14} strokeWidth={1.5} /> The Rewired Life dashboard
            </Link>
          </div>
        </nav>
        <div className="p-6 border-t border-hairline">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 text-ink-soft hover:text-ink text-sm w-full"
            data-testid="tmi-sidebar-logout"
          >
            <LogOut size={14} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-5xl">
          <div className="lg:hidden flex items-center justify-between mb-10">
            <Link to="/" className="font-serif-display text-xl text-ink">
              Pip<span className="text-gold italic"> Bennett</span>
            </Link>
            <button onClick={handleLogout} className="text-ink-soft text-sm" data-testid="tmi-mobile-logout">
              Sign out
            </button>
          </div>

          <div className="eyebrow mb-4 text-sage">Welcome</div>
          <h1
            className="font-serif-display text-4xl md:text-6xl text-ink leading-[1.05]"
            data-testid="tmi-dashboard-title"
          >
            The <em className="italic text-sage">Menopause</em> Invitation.
          </h1>
          <p className="mt-6 text-ink-soft max-w-xl leading-relaxed">
            {hasAccess
              ? `Hello ${user?.name || "lovely"}. Move at your own pace. Your written reflections save automatically as you go — they'll be here whenever you return.`
              : "You're almost in. Unlock the program to access all six modules, the welcome orientation, and your auto-saving reflection journal."}
          </p>

          {!hasAccess && (
            <div
              className="mt-10 bg-sage text-cream rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between"
              data-testid="tmi-dashboard-upgrade"
            >
              <div>
                <div className="eyebrow mb-2 text-gold">Unlock</div>
                <h2 className="font-serif-display text-2xl md:text-3xl">The Menopause Invitation</h2>
                <p className="mt-3 text-cream/75 max-w-md">
                  Lifetime access to all six modules and your private reflection journal for $87 AUD.
                </p>
              </div>
              <Link
                to="/the-menopause-invitation"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink rounded-full px-8 py-4 font-medium whitespace-nowrap transition-all"
                data-testid="tmi-dashboard-upgrade-cta"
              >
                Begin — $87
                <ArrowRight size={16} strokeWidth={1.8} />
              </Link>
            </div>
          )}

          {hasAccess && totalQuestions > 0 && (
            <div className="mt-10 bg-white border border-hairline rounded-2xl p-6 md:p-8 flex items-center gap-6" data-testid="tmi-progress">
              <div className="font-serif-display text-4xl md:text-5xl text-sage tabular-nums">
                {answeredCount}
                <span className="text-ink-soft text-2xl">/{totalQuestions}</span>
              </div>
              <div className="flex-1">
                <div className="eyebrow text-sage">Reflections answered</div>
                <p className="mt-1 text-ink-soft text-sm">
                  Quiet, considered work. There&apos;s no rush — and no right answer.
                </p>
              </div>
            </div>
          )}

          {/* Live cohort session recordings — visible to TMI Live members
              (paid Live, Co-Creators, or founding-member comp) AND admins
              (so Pip can preview her own materials without granting herself
              cohort access). */}
          {(user?.has_tmi_live_access || user?.role === "admin") && (
            <div
              className="mt-10 bg-vibrant/8 border border-vibrant/30 rounded-2xl p-6 md:p-8"
              data-testid="tmi-live-recordings"
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[0.65rem] tracking-[0.18em] uppercase font-bold text-vibrant-dark">
                  Live cohort · Aug 2026
                </span>
              </div>
              <h2 className="font-serif-display text-2xl md:text-3xl text-ink">
                Live session recordings
              </h2>
              <p className="mt-2 text-ink-soft text-sm max-w-2xl leading-relaxed">
                Each week&apos;s teaching portion is posted here after the live call. The live
                coaching and Q&amp;A stays private to the cohort.
              </p>

              <div className="mt-6 space-y-4">
                {/* Week 1 — audio + downloadable slides & workbook */}
                <div
                  className="bg-white border border-hairline rounded-2xl p-5 md:p-6"
                  data-testid="tmi-live-week-1"
                >
                  <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.18em] uppercase font-semibold text-vibrant-dark mb-2">
                    Week 1 · Session 1
                  </div>
                  <p className="text-xs text-ink-soft mb-4 italic">
                    Session recording (audio only this week — visuals return from Week 2), plus the
                    slides and workbook to work through in your own time.
                  </p>
                  <audio
                    controls
                    preload="none"
                    className="w-full"
                    data-testid="tmi-live-week-1-audio"
                    src="https://customer-assets-v7afamib.emergentagent.net/job_pip-revamp/artifacts/9mbj9dc7_TMI%20Live%20Session%201.m4a"
                  >
                    Your browser doesn&apos;t support the audio player.{" "}
                    <a
                      href="https://customer-assets-v7afamib.emergentagent.net/job_pip-revamp/artifacts/9mbj9dc7_TMI%20Live%20Session%201.m4a"
                      className="text-sage-dark underline"
                    >
                      Download the audio.
                    </a>
                  </audio>

                  {/* Slides + Workbook downloads. Both are gated on the backend
                      by has_tmi_live_access — the endpoints return 403 for
                      non-members even if the URL leaks. */}
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/tmi-live/materials/1/workbook`}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex items-start gap-3 bg-cream-soft hover:bg-cream border border-hairline rounded-xl px-4 py-3 transition"
                      data-testid="tmi-live-week-1-workbook"
                    >
                      <div className="w-9 h-9 rounded-full bg-sage/12 text-sage inline-flex items-center justify-center flex-shrink-0">
                        <BookOpen size={16} strokeWidth={1.6} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[0.65rem] tracking-[0.14em] uppercase font-semibold text-sage">
                          Workbook · PDF
                        </div>
                        <div className="text-sm text-ink font-medium truncate">
                          Week 1 Workbook
                        </div>
                        <div className="text-[11px] text-ink-soft/70 mt-0.5">
                          Opens on any device
                        </div>
                      </div>
                      <Download
                        size={14}
                        strokeWidth={1.8}
                        className="text-ink-soft group-hover:text-ink transition mt-1"
                      />
                    </a>

                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/tmi-live/materials/1/slides`}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex items-start gap-3 bg-cream-soft hover:bg-cream border border-hairline rounded-xl px-4 py-3 transition"
                      data-testid="tmi-live-week-1-slides"
                    >
                      <div className="w-9 h-9 rounded-full bg-gold/15 text-gold-dark inline-flex items-center justify-center flex-shrink-0">
                        <FileText size={16} strokeWidth={1.6} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[0.65rem] tracking-[0.14em] uppercase font-semibold text-gold-dark">
                          Slides · Keynote
                        </div>
                        <div className="text-sm text-ink font-medium truncate">
                          Week 1 Live Slides
                        </div>
                        <div className="text-[11px] text-ink-soft/70 mt-0.5">
                          Requires Keynote (Mac / iOS / iCloud)
                        </div>
                      </div>
                      <Download
                        size={14}
                        strokeWidth={1.8}
                        className="text-ink-soft group-hover:text-ink transition mt-1"
                      />
                    </a>
                  </div>
                </div>

                {/* Week 2 — audio replay + downloadable workbook */}
                <div
                  className="bg-white border border-hairline rounded-2xl p-5 md:p-6"
                  data-testid="tmi-live-week-2"
                >
                  <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.18em] uppercase font-semibold text-vibrant-dark mb-2">
                    Week 2 · Your Changing Body
                  </div>
                  <p className="text-xs text-ink-soft mb-4 italic">
                    Audio replay of the live session, plus the Week 2 workbook to work through in
                    your own time.
                  </p>
                  <audio
                    controls
                    preload="none"
                    className="w-full"
                    data-testid="tmi-live-week-2-audio"
                    src="https://customer-assets-v7afamib.emergentagent.net/job_pip-revamp/artifacts/gw7qg5b3_Week%202%20TMI.m4a"
                  >
                    Your browser doesn&apos;t support the audio player.{" "}
                    <a
                      href="https://customer-assets-v7afamib.emergentagent.net/job_pip-revamp/artifacts/gw7qg5b3_Week%202%20TMI.m4a"
                      className="text-sage-dark underline"
                    >
                      Download the audio.
                    </a>
                  </audio>

                  <div className="mt-5">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/tmi-live/materials/2/workbook`}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex items-start gap-3 bg-cream-soft hover:bg-cream border border-hairline rounded-xl px-4 py-3 transition"
                      data-testid="tmi-live-week-2-workbook"
                    >
                      <div className="w-9 h-9 rounded-full bg-sage/12 text-sage inline-flex items-center justify-center flex-shrink-0">
                        <BookOpen size={16} strokeWidth={1.6} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[0.65rem] tracking-[0.14em] uppercase font-semibold text-sage">
                          Workbook · PDF
                        </div>
                        <div className="text-sm text-ink font-medium truncate">
                          Week 2 Workbook — Your Changing Body
                        </div>
                        <div className="text-[11px] text-ink-soft/70 mt-0.5">
                          Opens on any device
                        </div>
                      </div>
                      <Download
                        size={14}
                        strokeWidth={1.8}
                        className="text-ink-soft group-hover:text-ink transition mt-1"
                      />
                    </a>
                  </div>
                </div>

                {/* Placeholder tiles for weeks 3–4 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {["Week 3", "Week 4"].map((label) => (
                    <div
                      key={label}
                      className="bg-cream border border-dashed border-hairline rounded-xl px-4 py-3"
                      data-testid={`tmi-live-${label.toLowerCase().replace(" ", "-")}-upcoming`}
                    >
                      <div className="text-[0.65rem] tracking-[0.18em] uppercase font-semibold text-ink-soft">
                        {label}
                      </div>
                      <div className="mt-0.5 text-xs text-ink-soft/70 italic">
                        Coming after the live call
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {hasAccess && !user?.founding_member && (
            <ReferralCard
              referralCode={user?.referral_code || ""}
              credit={Number(user?.referral_credit_aud || 0)}
            />
          )}

          <div className="mt-14">
            <div className="eyebrow mb-5 text-sage">Your Modules</div>
            {loading ? (
              <div className="text-ink-soft">Loading…</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {(modules || []).map((m, i) => {
                  const moduleAnswers = answers[m.id] || {};
                  const answered = Object.values(moduleAnswers).filter(
                    (v) => v && v.trim().length > 0,
                  ).length;
                  const total = m.questions?.length || 0;
                  return (
                    <Link
                      key={m.id}
                      to={hasAccess ? `/dashboard/menopause/module/${m.id}` : "/the-menopause-invitation"}
                      className="group bg-white border border-hairline rounded-2xl p-8 hover:border-sage hover:shadow-[0_20px_40px_-20px_rgba(74,93,79,0.15)] transition-all"
                      data-testid={`tmi-dashboard-module-${m.id}`}
                    >
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div className="eyebrow text-sage">{m.subtitle || `Module ${i}`}</div>
                        {!hasAccess && <Lock size={14} strokeWidth={1.5} className="text-ink-soft" />}
                      </div>
                      <h3 className="font-serif-display text-2xl text-ink leading-tight">{m.title}</h3>
                      <p className="mt-3 text-sm text-ink-soft leading-relaxed line-clamp-3">
                        {m.description}
                      </p>
                      <div className="mt-6 flex items-center gap-5 text-xs text-ink-soft">
                        <span className="inline-flex items-center gap-1.5">
                          <BookOpen size={12} strokeWidth={1.5} /> {m.duration || "—"}
                        </span>
                        {hasAccess && total > 0 && (
                          <span className="inline-flex items-center gap-1.5">
                            <Pencil size={12} strokeWidth={1.5} /> {answered}/{total} reflections
                          </span>
                        )}
                      </div>
                      <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-sage group-hover:translate-x-1 transition">
                        {hasAccess ? "Open module" : "Unlock"}
                        <ArrowRight size={14} strokeWidth={1.8} />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
